import React, { useEffect, useMemo, useState } from "react";
import { Form, Col, Row, Spinner, FormText } from "react-bootstrap";
import { Helmet } from "react-helmet";
import CardComponent from "../../components/CardComponent";
import Select from "react-select";
import { ErrorMessage, Formik } from "formik";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import TextareaAutosize from "react-textarea-autosize";
import { useTranslation } from "react-i18next";
import ConfirmAlert from "../../components/ConfirmAlert";
import {
  getallAreaManagerInEnergyCompany,
  getAreaManagerInEnergyCompanyById,
  postEnergyCompany,
  updateEnergyCompany,
  getAllEnergyTeamMembers,
  deleteEnergyCompanyById,
} from "../../services/contractorApi";
import { toast } from "react-toastify";
import { getAllEneryComnies } from "../../services/authapi";
import { addEnergyCompanySchema } from "../../utils/formSchema";
// List-view dependencies (additive — used only when view === "list")
import { createColumnHelper } from "@tanstack/react-table";
import CustomTable from "../../components/DataTable/CustomTable";
import TableHeader from "../../components/DataTable/TableHeader";
import ActionButtons from "../../components/DataTable/ActionButtons";
import { MdAddCard } from "react-icons/md";
import { serialNumber, onlyDecimalKey, handleDecimalPaste } from "../../utils/helper";
import DatePickercomp from "../../components/DatePicker";

const CreateTeam = ({ checkPermission }) => {
  // Existing form state (preserved exactly)
  const [edit, setEdit] = useState({});
  const [searchParams] = useSearchParams();
  const urlType = searchParams.get("type") || null;
  const [showAlert, setShowAlert] = useState(false);
  const [allAreaName, setAllAreaName] = useState([]);
  const [allEnergy, setAllEnergy] = useState([]);
  const [energyCompanyId, setEnergyCompanyId] = useState("");
  const { id } = useParams();
  const location = useLocation();
  const energyId = location?.state?.energy_company_id;

  const { t } = useTranslation();
  const navigate = useNavigate();

  // ── List/Form toggle state (additive) ───────────────────────────────────
  // Default view = LIST of all existing energy team members.
  // Clicking the "+" icon switches to the existing CREATE form.
  // Row View / Edit also switch to the form (prefilled).
  // After form save/cancel, view returns to LIST and the list refreshes.
  const [view, setView] = useState("list");
  // formMode controls read-only behaviour of the existing form
  // (preserved exactly via the `type` shim below).
  const [formMode, setFormMode] = useState("create"); // "create" | "edit" | "view"
  const type = formMode === "view" ? "view" : urlType;

  // List-view state (server-side search + pagination — future-proof for large data)
  const [rows, setRows] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const pageNo = searchParams.get("pageNo") || 1;
  const pageSize = searchParams.get("pageSize") || 10;
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const columnHelper = createColumnHelper();

  const fetchAllAreaManagerData = async (id, energy_company_id) => {
    const res = await getallAreaManagerInEnergyCompany(id, energy_company_id);
    if (res.status) {
      setAllAreaName(res.data);
    } else {
      setAllAreaName([]);
      toast.error(res.message);
    }
  };

  // Prefill the form from a clicked row (replaces the URL/:id-driven fetch
  // that the old useEffect used — that path was never wired on this tab).
  const fetchEnergyCompanyByRow = async (energy_company_id, user_id) => {
    const res = await getAreaManagerInEnergyCompanyById(
      energy_company_id,
      user_id,
    );
    if (res.status) {
      setEdit(res.data);
      if (res.data?.area_name_id) {
        fetchAllAreaManagerData(
          res.data.area_name_id,
          res.data.energy_company_id,
        );
      }
    } else {
      setEdit({});
    }
  };

  const fetchEnergyData = async () => {
    const res = await getAllEneryComnies();
    if (res.status) {
      const rData = res.data.map((itm) => {
        return {
          value: itm.energy_company_id,
          label: itm.name,
        };
      });
      setAllEnergy(rData);
    } else {
      setAllEnergy([]);
    }
  };

  // Server-side paginated + searched team list.
  const fetchList = async () => {
    setIsLoading(true);
    const res = await getAllEnergyTeamMembers({ search, pageSize, pageNo });
    if (res.status) {
      setRows(res.data || []);
      setTotalData(res?.pageDetails?.total || 0);
    } else {
      setRows([]);
      setTotalData(0);
    }
    setIsLoading(false);
  };

  // Companies needed for the form's Energy Company dropdown — load once.
  useEffect(() => {
    fetchEnergyData();
  }, []);

  // List refresh on view/search/pagination change (debounced via SearchHeader).
  useEffect(() => {
    if (view === "list") fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, search, pageNo, pageSize]);

  // ── List-view action handlers ───────────────────────────────────────────
  const handleCreate = () => {
    setEdit({});
    setFormMode("create");
    setShowAlert(false); // ensure no stale confirm-popup state from a previous edit
    setView("form");
  };
  const handleViewRow = (row) => {
    // Open the shared Employee view screen instead of the inline form.
    // ViewEmployee.js conditionally renders the Transfer History card based
    // on `transfer_timeline` returned by the backend for energy-team users.
    if (row?.user_id) {
      navigate(`/Employees/ViewEmployee/${row.user_id}`);
    }
  };
  const handleEditRow = async (row) => {
    setFormMode("edit");
    setShowAlert(false); // ensure no stale confirm-popup state from a previous edit
    await fetchEnergyCompanyByRow(row.energy_company_id, row.user_id);
    setView("form");
  };
  const handleDeleteRow = (row) => {
    setDeleteId(row.user_id);
    setShowDeleteAlert(true);
  };
  const handleConfirmDelete = async () => {
    if (!deleteId) return;
    const res = await deleteEnergyCompanyById(deleteId);
    setShowDeleteAlert(false);
    setDeleteId(null);
    if (res.status) {
      toast.success(res.message);
      fetchList();
    } else {
      toast.error(res.message);
    }
  };
  const handleBackToList = () => {
    setEdit({});
    setFormMode("create");
    setView("list");
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const sData = {
      email: values.email,
      username: values.username,
      password: values.password,
      contact_no: String(values.contact_no),
      alt_number: values.alt_number,
      address: values.address,
      status: values.status.value,
      country: values.country,
      city: values.city,
      pin_code: values.pin_code,
      joining_date: values.joining_date,
      area_name: values.area_name?.value,
      area_selected: values.area_selected?.value,
      energy_company_id: values.energy_company_id.value,
      description: values.description,
      transfer_date: values?.transfer_date,
    };
    if (edit.user_id) {
      sData["id"] = edit.user_id;
    }

    const res = edit?.user_id
      ? await updateEnergyCompany(sData)
      : await postEnergyCompany(sData);
    // Always close the "Confirm Update" popup once submit completes —
    // success OR failure. Without this the alert state leaks: the next time
    // the user opens the form the popup auto-shows because `showAlert`
    // stayed true from the previous confirm.
    setShowAlert(false);

    if (res.status) {
      toast.success(res.message);
      resetForm();
      // Return to list (instead of navigate(-1) which would leave this tab)
      // and refresh so the new/updated row appears immediately.
      setEdit({});
      setFormMode("create");
      setView("list");
      fetchList();
    } else {
      toast.error(res.message);
    }

    setSubmitting(false);
  };

  // ── List columns ────────────────────────────────────────────────────────
  const columns = useMemo(
    () => [
      columnHelper.accessor("sr_no", {
        header: t("Sr No."),
        cell: (info) => {
          const nums = serialNumber(pageNo, pageSize);
          return nums[info.row.index];
        },
      }),
      columnHelper.accessor("username", { header: t("Name") }),
      columnHelper.accessor("email", { header: t("Email") }),
      columnHelper.accessor("mobile", { header: t("Mobile") }),
      columnHelper.accessor("energy_company_name", {
        header: t("Energy Company"),
      }),
      columnHelper.accessor("area_selected", {
        header: t("Area Name"),
        cell: (info) => {
          const r = info.row.original;
          const label = r?.area_name ? String(r.area_name).replace(/_/g, " ") : "";
          const val = r?.area_selected || "";
          return label && val ? `${label.toUpperCase()} — ${val}` : val || "-";
        },
      }),
      columnHelper.accessor("status", {
        header: t("Status"),
        cell: (info) =>
          info.row.original?.status == 1 || info.row.original?.status === "1"
            ? "Active"
            : "Inactive",
      }),
      columnHelper.accessor("action", {
        header: t("Action"),
        cell: (info) => (
          <ActionButtons
            actions={{
              // Oil and Gas > Energy Team row: View (reaching the tab), Edit,
              // Delete — and Add on the toolbar (2026-09-19; before that every
              // button ignored the row's rights).
              view: {
                show: true,
                action: () => handleViewRow(info.row.original),
              },
              edit: {
                show: Number(checkPermission?.update) > 0,
                action: () => handleEditRow(info.row.original),
              },
              delete: {
                show: Number(checkPermission?.delete) > 0,
                action: () => handleDeleteRow(info.row.original),
              },
            }}
          />
        ),
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checkPermission, t, rows.length, pageNo, pageSize],
  );
  return (
    <>
      <Helmet>
        <title>Create Energy team management · CMS Electricals </title>
      </Helmet>
      {view === "list" ? (
        <Col md={12} data-aos={"fade-up"}>
          <CustomTable
            id={"energy_team_members"}
            userPermission={checkPermission}
            isLoading={isLoading}
            rows={rows || []}
            columns={columns}
            pagination={{ pageNo, pageSize, totalData }}
            customHeaderComponent={() => (
              <TableHeader
                userPermission={checkPermission}
                setSearchText={setSearch}
                button={{ show: false }}
                extraComponent={
                  Number(checkPermission?.create) > 0 ? (
                    <span
                      title={t("Energy Team")}
                      onClick={handleCreate}
                      className="fs-2 text-success cursor-pointer ms-2"
                      role="button"
                    >
                      <MdAddCard />
                    </span>
                  ) : null
                }
              />
            )}
            tableTitleComponent={
              <div>
                <strong>{t("Energy Company Members")}</strong>
              </div>
            }
          />
          <ConfirmAlert
            size={"sm"}
            show={showDeleteAlert}
            hide={setShowDeleteAlert}
            deleteFunction={handleConfirmDelete}
            title={"Confirm Delete"}
            description={"Are you sure you want to delete this team member?"}
          />
        </Col>
      ) : (
        <>
      <Col md={12}>
        <CardComponent showCardHeader={true}
          showBackButton={true}
          onBack={handleBackToList}
          title={`${type === "view" ? "View" : edit.user_id ? "Update" : "Create"
            } Energy team member`}
          className={"my-2"}
        >
          <Col md={12}>
            <Formik
              enableReinitialize={true}
              initialValues={{
                energy_company_id: edit?.energy_company_id
                  ? {
                    label: edit?.energy_company_name,
                    value: edit?.energy_company_id,
                  }
                  : "",
                email: edit.email || "",
                username: edit.username || "",
                password: edit.password || "",
                contact_no: edit.mobile || "",
                alt_number: edit.alt_number || "",
                address: edit.address || "",
                status: edit.status
                  ? {
                    label: edit.status == "1" ? "Active" : "InActive",
                    value: parseInt(edit.status),
                  }
                  : { label: "Active", value: 1 },
                joining_date: edit.joining_date || "",
                area_name: edit?.area_name
                  ? {
                    label: edit?.area_name,
                    value: edit?.area_name_id,
                  }
                  : "",
                area_selected: edit?.area_selected
                  ? {
                    label: edit?.area_selected,
                    value: edit?.area_selected_id,
                  }
                  : "",
                country: edit.country || "",
                city: edit.city || "",
                pin_code: edit.pin_code || "",
                description: edit.description || "",
                // Default to TODAY (local date, YYYY-MM-DD) for both create
                // and edit when no stored value exists. `toLocaleDateString
                // ("en-CA")` returns the local-timezone ISO date so the
                // HTML date input renders correctly in any timezone.
                transfer_date:
                  edit.transfer_date ||
                  new Date().toLocaleDateString("en-CA"),
              }}
              validationSchema={addEnergyCompanySchema}
              // Edit-only required validation for Transfer Date.
              // Yup schema (`addEnergyCompanySchema`) is shared and does not
              // include transfer_date, so we enforce it here based on the
              // current mode. Create mode -> field is optional.
              validate={(values) => {
                const errors = {};
                if (edit?.user_id) {
                  const v = values?.transfer_date;
                  if (!v || String(v).trim() === "") {
                    errors.transfer_date = "Transfer Date is required";
                  }
                }
                return errors;
              }}
              onSubmit={handleSubmit}
            >
              {(props) => (
                <Form onSubmit={props.handleSubmit}>
                  <Row className="mt-2 g-2">
                    <Form.Group as={Col} md="4">
                      <Form.Label>
                        {t("Energy Company Name")}
                        <span className="text-danger fw-bold">*</span>
                      </Form.Label>
                      <Select
                        menuPortalTarget={document.body}
                        className="text-primary"
                        name="energy_company_id"
                        value={props.values.energy_company_id}
                        onChange={(val) => {
                          props.setFieldValue("energy_company_id", val);
                          setEnergyCompanyId(val.value);
                        }}
                        onBlur={props.handleBlur}
                        options={allEnergy}
                        isInvalid={Boolean(
                          props.touched.energy_company_id &&
                          props.errors.energy_company_id
                        )}
                      />
                      <ErrorMessage
                        name="energy_company_id"
                        component="small"
                        className="text-danger"
                      />
                    </Form.Group>
                    <Form.Group as={Col} md="4">
                      <Form.Label>
                        {t("username")} <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name={"username"}
                        value={props.values.username}
                        onChange={props.handleChange}
                        onBlur={props.handleBlur}
                        isInvalid={Boolean(
                          props.touched.username && props.errors.username
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {props.errors.username}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group as={Col} md="4">
                      <Form.Label>
                        {t("contact no")} <span className="text-danger">*</span>
                      </Form.Label>
                      <Form.Control
                        type="text" onKeyPress={onlyDecimalKey} onPaste={handleDecimalPaste} inputMode="decimal"
                        maxLength={10}
                        name={"contact_no"}
                        value={props.values.contact_no}
                        onChange={props.handleChange}
                        onBlur={props.handleBlur}
                        isInvalid={Boolean(
                          props.touched.contact_no && props.errors.contact_no
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {props.errors.contact_no}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group as={Col} md="4">
                      <Form.Label>{t("alt number")}</Form.Label>
                      <Form.Control
                        type="text"
                        maxLength={10}
                        name={"alt_number"}
                        value={props.values.alt_number}
                        onChange={props.handleChange}
                        onBlur={props.handleBlur}
                        isInvalid={Boolean(
                          props.touched.alt_number && props.errors.alt_number
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {props.errors.alt_number}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group as={Col} md="4">
                      <Form.Label>{t("Status")}</Form.Label>
                      <Select
                        menuPortalTarget={document.body}
                        name={"status"}
                        options={[
                          { label: "Active", value: 1 },
                          { label: "Inactive", value: 0 },
                        ]}
                        value={props.values.status}
                        onChange={(selectedOption) => {
                          props.setFieldValue("status", selectedOption);
                        }}
                      />
                    </Form.Group>
                    <Form.Group as={Col} md="4">
                      <Form.Label>{t("country")}</Form.Label>
                      <Form.Control
                        type="text"
                        name={"country"}
                        value={props.values.country}
                        onChange={props.handleChange}
                        onBlur={props.handleBlur}
                        isInvalid={Boolean(
                          props.touched.country && props.errors.country
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {props.errors.country}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group as={Col} md="4">
                      <Form.Label>{t("city")}</Form.Label>
                      <Form.Control
                        type="text"
                        name={"city"}
                        value={props.values.city}
                        onChange={props.handleChange}
                        onBlur={props.handleBlur}
                        isInvalid={Boolean(
                          props.touched.city && props.errors.city
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {props.errors.city}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group as={Col} md="4">
                      <Form.Label>{t("pin code")}</Form.Label>
                      <Form.Control
                        type="text"
                        name={"pin_code"}
                        value={props.values.pin_code}
                        onChange={props.handleChange}
                        onBlur={props.handleBlur}
                        isInvalid={Boolean(
                          props.touched.pin_code && props.errors.pin_code
                        )}
                      />
                      <Form.Control.Feedback type="invalid">
                        {props.errors.pin_code}
                      </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group as={Col} md="4">
                      <Form.Label>
                        {t("Joining Date")}{" "}
                        {/* <span className="text-danger">*</span> */}
                      </Form.Label>
                      <DatePickercomp
                        value={props.values.joining_date}
                        sendDate={(date) =>
                          props.setFieldValue("joining_date", date)
                        }
                      />
                      <ErrorMessage
                        name="joining_date"
                        component="small"
                        className="text-danger"
                      />
                    </Form.Group>
                    <Form.Group as={Col} md="4">
                      <Form.Label>
                        {t("Area Name")} <span className="text-danger">*</span>
                      </Form.Label>
                      <Select
                        className="text-primary w-100"
                        menuPortalTarget={document.body}
                        name="area_name"
                        options={[
                          {
                            label: "Zone",
                            value: 1,
                          },
                          {
                            label: "Regional",
                            value: 2,
                          },
                          {
                            label: "Sales Area",
                            value: 3,
                          },
                          {
                            label: "District",
                            value: 4,
                          },
                          {
                            label: "Outlets",
                            value: 5,
                          },
                        ]}
                        value={props.values?.area_name}
                        onChange={(val) => {
                          props.setFieldValue("area_name", val);
                          props.setFieldValue("area_selected", "");
                          fetchAllAreaManagerData(
                            val.value,
                            props.values?.energy_company_id?.value ||
                              energyCompanyId ||
                              energyId
                          );
                        }}
                      />
                      <ErrorMessage
                        name="area_name"
                        component="small"
                        className="text-danger"
                      />
                    </Form.Group>
                    {props.values?.area_name.label ? (
                      <Form.Group as={Col} md="4">
                        <Form.Label>
                          {props.values?.area_name.label}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Select
                          menuPortalTarget={document.body}
                          name={"area_selected"}
                          options={allAreaName.map((data) => ({
                            label: data.area_name,
                            value: data.id,
                          }))}
                          value={props.values.area_selected}
                          onChange={(selectedOption) => {
                            props.setFieldValue(
                              "area_selected",
                              selectedOption
                            );
                          }}
                        />
                        <ErrorMessage
                          name="area_selected"
                          component="small"
                          className="text-danger"
                        />
                      </Form.Group>
                    ) : null}
                    {/* Transfer Date — always rendered now (was hidden on
                        create). Sits in the same row as Area Name / Sales
                        Area, so when Sales Area appears the flex layout
                        shifts Transfer Date one column to the right
                        automatically. The required asterisk + validation
                        kick in only in EDIT mode (Formik `validate` prop). */}
                    <Form.Group as={Col} md="4">
                      <Form.Label>
                        {t("Transfer Date")}
                        {edit.user_id ? (
                          <span className="text-danger"> *</span>
                        ) : null}
                      </Form.Label>
                      <DatePickercomp
                        value={props.values.transfer_date}
                        sendDate={(date) =>
                          props.setFieldValue("transfer_date", date)
                        }
                      />
                      <ErrorMessage
                        name="transfer_date"
                        component="small"
                        className="text-danger"
                      />
                    </Form.Group>

                    <Form.Group as={Col} md="12">
                      <Form.Label>{t("address")}</Form.Label>
                      <TextareaAutosize
                        minRows={2}
                        className="edit-textarea"
                        name={"address"}
                        value={props.values.address}
                        onChange={props.handleChange}
                      />
                    </Form.Group>

                    <Form.Group as={Col} md="12">
                      <Form.Label>{t("Description")}</Form.Label>
                      <TextareaAutosize
                        minRows={2}
                        className="edit-textarea"
                        name={"description"}
                        value={props.values.description}
                        onChange={props.handleChange}
                      />
                    </Form.Group>
                    <Form.Group as={Col} md={12}>
                      <div className="shadow mt-2 p-3">
                        <Form.Group as={Row} className="mb-3">
                          <Form.Label column>
                            {t("User Email")}
                            <span className="text-danger fw-bold">*</span>
                          </Form.Label>
                          <Col sm={8}>
                            <Form.Control
                              type="email"
                              value={props.values.email}
                              onChange={props.handleChange}
                              name={"email"}
                              onBlur={props.handleBlur}
                              isInvalid={Boolean(
                                props.touched.email && props.errors.email
                              )}
                            />
                            <Form.Control.Feedback type="invalid">
                              {props.errors.email}
                            </Form.Control.Feedback>
                          </Col>
                        </Form.Group>
                        <Form.Group as={Row} className="mb-3">
                          <Form.Label column>
                            {t("Password")}
                            {edit?.id ? null : (
                              <span className="text-danger fw-bold">*</span>
                            )}
                          </Form.Label>
                          <Col sm={8}>
                            <div className="d-grid gap-1">
                              <Form.Control
                                type="password"
                                value={props.values.password}
                                onChange={props.handleChange}
                                name="password"
                                onBlur={props.handleBlur}
                                isInvalid={Boolean(
                                  props.touched.password &&
                                  props.errors.password
                                )}
                              />
                              <Form.Control.Feedback type="invalid">
                                {props.errors.password}
                              </Form.Control.Feedback>
                              {edit?.user_id ? (
                                <FormText>
                                  {t(
                                    "Password is encrypted. If you don't want to change it, leave it blank"
                                  )}
                                  .
                                </FormText>
                              ) : null}
                            </div>
                          </Col>
                        </Form.Group>
                      </div>
                    </Form.Group>
                    <Col md={12}>
                      <div className="mt-4 text-center">
                        <button
                          type={`${edit.user_id ? "button" : "submit"}`}
                          onClick={() => setShowAlert(edit.user_id && true)}
                          disabled={props?.isSubmitting}
                          className="shadow border-0 purple-combo cursor-pointer px-4 py-1"
                        >
                          {props?.isSubmitting ? (
                            <>
                              <Spinner
                                animation="border"
                                variant="primary"
                                size="sm"
                              />
                              {t("PLEASE WAIT")}...
                            </>
                          ) : (
                            <>{edit.user_id ? t("UPDATE") : t("CREATE")}</>
                          )}
                        </button>
                        <ConfirmAlert
                          size={"sm"}
                          deleteFunction={props.handleSubmit}
                          hide={setShowAlert}
                          show={showAlert}
                          title={"Confirm UPDATE"}
                          description={"Are you sure you want to update this!!"}
                        />
                      </div>
                    </Col>
                  </Row>
                </Form>
              )}
            </Formik>
          </Col>
        </CardComponent>
      </Col>
        </>
      )}
    </>
  );
};

export default CreateTeam;
